/**
 * Composable for managing table of contents with scroll spy functionality.
 * Tracks visible headings and provides smooth scroll navigation.
 */
import { nextTick, onBeforeUnmount, ref, watch, type Ref } from 'vue';
import type { TableOfContentsHeading } from '~/types';
import {
  buildTableOfContentsFromContent,
  getHeadingElements,
} from '~/utils/tableOfContents';

/**
 * Options for configuring the heading TOC behavior.
 */
interface UseHeadingTocOptions {
  /** Offset from top of viewport for scroll spy (default: 50) */
  scrollSpyOffsetTop?: number | (() => number);
  /** Intersection observer rootMargin bottom (default: '-85%') */
  observerBottomMargin?: string;
  /** Padding when scrolling to heading (default: 50) */
  selectScrollPadding?: number | (() => number);
  /** Lock duration after programmatic scroll in ms (default: 700) */
  programmaticLockMs?: number;
  /** Whether to observe characterData mutations (default: true) */
  observeCharacterData?: boolean;
}

/**
 * Creates a table of contents with scroll spy functionality.
 * @param contentRef - Reference to the content container element
 * @param tableOfContentsListRef - Reference to the TOC list element
 * @param options - Configuration options
 * @returns Object with reactive state and control methods
 */

export function useHeadingToc(
  contentRef: Ref<HTMLElement | null>,
  tableOfContentsListRef: Ref<HTMLElement | null>,
  options: UseHeadingTocOptions = {}
) {
  const headingSelector = '.dynamic-anchor';
  const scrollSpyOffsetTop = options.scrollSpyOffsetTop ?? 50;
  const selectScrollPadding = options.selectScrollPadding ?? 50;
  const programmaticLockMs = options.programmaticLockMs ?? 700;
  const observeCharacterData = options.observeCharacterData ?? true;
  let headings = null as unknown as HTMLElement[];

  const tableOfContentsHeadings = ref<TableOfContentsHeading[]>([]);
  const activeHeadingId = ref<string | null>(null);

  let headingObserver: IntersectionObserver | null = null;
  let contentObserver: MutationObserver | null = null;
  let programmaticScrollLockUntil = 0;
  let programmaticHeadingId: string | null = null;
  let scrollRaf = 0;

  const resolveOffset = (offset: number | (() => number)): number =>
    typeof offset === 'function' ? offset() : offset;

  // The IntersectionObserver only fires on viewport enter/leave, which isn't
  // frequent enough to track the offset line precisely while scrolling. Drive
  // recomputation from a passive, rAF-throttled scroll listener instead.
  function handleScroll(): void {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      setActiveHeadingFromVisible();
    });
  }

  function setActiveHeadingFromVisible(): void {
    if (Date.now() < programmaticScrollLockUntil && programmaticHeadingId) {
      activeHeadingId.value = programmaticHeadingId;
      return;
    }

    if (!tableOfContentsHeadings.value.length) {
      activeHeadingId.value = null;
      return;
    }

    // Active heading = the lowest heading whose top has scrolled above the
    // offset line (i.e. up under the sticky header). Computed from live geometry
    // so `scrollSpyOffsetTop` behaves as the literal trigger offset, and so it
    // can't freeze the way a thin-band IntersectionObserver does.
    const passed = [...headings]
      .reverse()
      .find(
        (el) =>
          el.getBoundingClientRect().top <= resolveOffset(scrollSpyOffsetTop)
      );

    if (passed?.id) {
      activeHeadingId.value = passed.id;
      return;
    }

    // Above the first heading — nothing has crossed the line yet.
    activeHeadingId.value =
      headings[0]?.id ?? tableOfContentsHeadings.value[0]?.id ?? null;
  }

  function updateTrackerPosition(): void {
    if (!tableOfContentsListRef.value) return;

    if (!activeHeadingId.value) {
      return;
    }

    const activeLi = tableOfContentsListRef.value.querySelector<HTMLElement>(
      `[data-heading-id="${activeHeadingId.value}"]`
    );
    if (!activeLi) return;
  }

  function setupHeadingObserver(container: HTMLElement | null): void {
    if (!container || !import.meta.client) return;

    headingObserver?.disconnect();

    // The observer is a backup trigger for enter/leave events; the scroll
    // listener drives precise updates. Both just recompute from live geometry.
    headingObserver = new IntersectionObserver(
      () => setActiveHeadingFromVisible(),
      {
        root: null,
        rootMargin: `-${resolveOffset(scrollSpyOffsetTop)}px 0px 0px 0px`,
        threshold: [0, 1],
      }
    );

    headings = getHeadingElements(contentRef.value, headingSelector);

    for (const heading of headings) {
      headingObserver.observe(heading);
    }

    setActiveHeadingFromVisible();
  }

  function syncTableOfContentsAndObserver(): void {
    tableOfContentsHeadings.value = buildTableOfContentsFromContent(
      contentRef.value,
      headingSelector
    );
    setupHeadingObserver(contentRef.value);
    nextTick(updateTrackerPosition);
  }

  function setupContentObserver(container: HTMLElement | null): void {
    if (!container || !import.meta.client) return;

    contentObserver?.disconnect();
    contentObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        syncTableOfContentsAndObserver();
      });
    });

    contentObserver.observe(container, {
      subtree: true,
      childList: true,
      characterData: observeCharacterData,
      characterDataOldValue: observeCharacterData,
    });
  }

  watch(activeHeadingId, () => nextTick(updateTrackerPosition));

  /**
   * Scrolls to a heading by ID with smooth animation.
   * @param headingId - The ID of the heading to scroll to
   */
  function scrollToHeading(headingId: string | null): void {
    if (!headingId || !import.meta.client) return;
    const heading = document.getElementById(headingId);
    if (!heading) return;

    programmaticHeadingId = headingId;
    programmaticScrollLockUntil = Date.now() + programmaticLockMs;
    activeHeadingId.value = headingId;

    nextTick(() => {
      const scrollPadding = resolveOffset(selectScrollPadding);
      const afterScroll = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(updateTrackerPosition);
        });
      };

      // Prefer GSAP's ScrollToPlugin: it is pin-aware. When a pinned
      // ScrollTrigger (e.g. the horizontal AnimatedCardContainer) sits between
      // the current position and the target, ScrollToPlugin adjusts the
      // destination so we still land on the correct heading. Native scrollTo has
      // no awareness of pinned sections, so it overshoots/undershoots once a
      // pinned section is in the scroll path.
      const gsap = useGSAP();
      if (gsap?.to) {
        gsap.to(window, {
          duration: 0.6,
          ease: 'power2.inOut',
          scrollTo: {
            y: heading,
            offsetY: scrollPadding,
            autoKill: true, // a manual scroll cancels the tween
          },
          onComplete: afterScroll,
          onInterrupt: afterScroll,
        });
        return;
      }

      // Fallback: native smooth scroll (not pin-aware).
      const top =
        heading.getBoundingClientRect().top + window.scrollY - scrollPadding;
      window.scrollTo({ top, behavior: 'smooth' });
      afterScroll();
    });
  }

  /** Initializes the observers and TOC - call on mount */
  function init(): void {
    if (!import.meta.client) return;
    nextTick(() => {
      setupContentObserver(contentRef.value);
      syncTableOfContentsAndObserver();
      window.addEventListener('scroll', handleScroll, { passive: true });

      requestAnimationFrame(() => {
        if (!tableOfContentsHeadings.value.length) {
          syncTableOfContentsAndObserver();
        }
      });
    });
  }

  /** Re-syncs headings and observers - call after content changes */
  function refresh(): void {
    if (!import.meta.client) return;
    nextTick(() => {
      syncTableOfContentsAndObserver();
    });
  }

  function destroy(): void {
    programmaticHeadingId = null;
    programmaticScrollLockUntil = 0;
    activeHeadingId.value = null;
    tableOfContentsHeadings.value = [];
    contentObserver?.disconnect();
    contentObserver = null;
    headingObserver?.disconnect();
    headingObserver = null;
    if (import.meta.client) {
      window.removeEventListener('scroll', handleScroll);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      scrollRaf = 0;
    }
  }

  onBeforeUnmount(destroy);

  return {
    activeHeadingId,
    init,
    refresh,
    scrollToHeading,
    tableOfContentsHeadings,
  };
}
